/**
 * RadialChart Component
 * Reusable radial bar chart for circular progress visualization
 */

"use client";

import {
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Label,
  PolarAngleAxis,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart as RechartsRadialBarChart,
} from "recharts";
import { ChartWrapper } from "./ChartWrapper";
import { RadialChartProps } from "./types";

export const RadialChart = <T extends Record<string, any>>({
  data,
  config,
  dataKey,
  nameKey,
  title,
  description,
  footer,
  className,
  containerClassName,
  height = 350,
  showTooltip = true,
  showLegend = false,
  tooltipConfig,
  legendConfig,
  innerRadius = "30%",
  outerRadius = "100%",
  startAngle = 90,
  endAngle = -270,
  cornerRadius = 10,
  centerLabel,
  accessibilityLayer = true,
  metricValue,
  metricChange,
  showMetricLayout = false,
  headerActions,
  secondaryDataKey,
  stacked = false,
  centerLabelFormatter,
  centerLabelSubtitle,
}: RadialChartProps) => {
  // Calculate display value for center label
  const primaryValue = (data[0]?.[dataKey] as number) || 0;
  const displayValue = centerLabelFormatter
    ? centerLabelFormatter(primaryValue)
    : centerLabel || primaryValue.toLocaleString();

  // Stacked mode uses ChartContainer directly (like RadialStatsCard)
  if (stacked && secondaryDataKey) {
    return (
      <ChartWrapper
        title={title}
        description={description}
        footer={footer}
        className={className}
        containerClassName={containerClassName}
        config={config}
        // height={height}
        metricValue={metricValue}
        metricChange={metricChange}
        showMetricLayout={showMetricLayout}
        headerActions={headerActions}
      >
        <RechartsRadialBarChart
          data={data}
          endAngle={endAngle}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          cx={"50%"}
          cy={"50%"}
        >
          {showTooltip && (
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent hideLabel={tooltipConfig?.hideLabel} />
              }
            />
          )}
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) - 10}
                        className="fill-foreground text-2xl font-bold"
                      >
                        {displayValue}
                      </tspan>
                      {centerLabelSubtitle && (
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 10}
                          className="fill-muted-foreground text-xs"
                        >
                          {centerLabelSubtitle}
                        </tspan>
                      )}
                    </text>
                  );
                }
              }}
            />
          </PolarRadiusAxis>
          <RadialBar
            dataKey={secondaryDataKey}
            stackId="a"
            cornerRadius={cornerRadius}
            fill={config[String(secondaryDataKey)]?.color || "var(--chart-2)"}
            className="stroke-transparent stroke-2"
          />
          <RadialBar
            dataKey={dataKey}
            stackId="a"
            cornerRadius={cornerRadius}
            fill={config[String(dataKey)]?.color || "var(--chart-1)"}
            className="stroke-transparent stroke-2"
          />
          {showLegend && (
            <ChartLegend
              content={<ChartLegendContent nameKey={String(nameKey)} />}
              className="-translate-y-2"
            />
          )}
        </RechartsRadialBarChart>
      </ChartWrapper>
    );
  }

  // Non-stacked mode (original implementation)
  const chartContent = (
    <RechartsRadialBarChart
      accessibilityLayer={accessibilityLayer}
      data={data}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      startAngle={startAngle}
      endAngle={endAngle}
    >
      <PolarAngleAxis
        type="number"
        domain={[0, 100]}
        angleAxisId={0}
        tick={false}
      />

      {showTooltip && (
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              hideLabel={tooltipConfig?.hideLabel}
              nameKey={tooltipConfig?.nameKey}
            />
          }
        />
      )}

      <RadialBar
        dataKey={dataKey}
        cornerRadius={cornerRadius}
        fill={config[String(dataKey)]?.color || "var(--chart-1)"}
        className="stroke-transparent stroke-2"
      >
        {/* Center label */}
        {centerLabel && (
          <Label
            value={centerLabel}
            position="center"
            className="fill-foreground text-2xl font-bold"
          />
        )}
      </RadialBar>

      {showLegend && (
        <ChartLegend
          content={<ChartLegendContent nameKey={String(nameKey)} />}
          className="-translate-y-2"
        />
      )}
    </RechartsRadialBarChart>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      footer={footer}
      className={className}
      containerClassName={containerClassName || "mx-auto aspect-square"}
      config={config}
      height={height}
      metricValue={metricValue}
      metricChange={metricChange}
      showMetricLayout={showMetricLayout}
      headerActions={headerActions}
    >
      {chartContent}
    </ChartWrapper>
  );
};

RadialChart.displayName = "RadialChart";

export default RadialChart;
